import { createPortal } from 'react-dom'
import { useDispatch } from '../../../Flux/index.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { dropFiles } from '../../../Services/OrderWizard/dropFiles.ts'
import type { DropTarget } from '../../../Services/OrderWizard/dropTargets.ts'
import { dropDisabled, dropPoolFor, dropTargets } from '../../../Services/OrderWizard/dropTargets.ts'
import { useAddFiles } from './AddFilesContext.ts'
import { DropZones } from './DropZones.tsx'
import { useFileDrag } from './useFileDrag.ts'

/* zones side by side up to this many, else wrapped in rows */
const OVERLAY_ROW = 4

/**
 * The whole page as a drop target, shown only while files are dragged over the window: one target
 * before the order has items, then one per pool and a new pool, the drop going where it lands.
 */
export const WizardDropOverlay = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const addFiles = useAddFiles()
  const { dragging, end } = useFileDrag()
  /* the open popover takes the drop itself, with its own zones */
  if (!dragging || addFiles.request) {
    return null
  }
  const targets = dropTargets(draft)
  const route = (target: DropTarget, files: File[]) => {
    end()
    dropFiles(draft.id, files, dispatch, dropPoolFor(draft, target))
  }
  return createPortal(
    <div
      className="fixed inset-0 z-70 flex flex-col gap-3 bg-slate-900/40 p-4 backdrop-blur-[2px] sm:p-8"
      data-testid="wizard-drop-overlay"
    >
      <DropZones
        testId="wizard-drop-target"
        variant="overlay"
        targets={targets}
        disabled={dropDisabled(draft)}
        onFiles={route}
        className={`mx-auto min-h-0 w-full max-w-5xl flex-1 gap-3 ${
          targets.length > OVERLAY_ROW ? 'flex-row flex-wrap content-stretch overflow-y-auto' : 'flex-col md:flex-row'
        }`}
        zoneClassName={targets.length > OVERLAY_ROW ? 'min-h-24 min-w-48' : undefined}
      />
      {targets.length > 1 && (
        <p className="mx-auto text-sm text-white" data-testid="wizard-drop-overlay-note">
          A sample sheet applies to the whole order, whichever target it lands on.
        </p>
      )}
    </div>,
    document.body
  )
}
